import { useState, useRef } from 'react';
import {
    Modal,
    Form,
    Input,
    Switch,
    Space,
    Button,
    message,
    Typography,
    Select,
    Tooltip,
    Dropdown,
    Menu,
    Col,
    Popover,
    Row,
} from 'antd';
import {
    EditOutlined,
    DeleteOutlined,
    PlusOutlined,
    LinkOutlined,
    CopyOutlined,
    InfoCircleOutlined,
    ReloadOutlined,
    EllipsisOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import type { ProColumns } from '@ant-design/pro-components';

import {
    getCalendars,
    createCalendar,
    updateCalendar,
    deleteCalendar,
    Calendar as APICalendar,
    syncCalendar,
} from 'utils/ez_api/calendarAPIs';
import { CalendarType } from 'types/calendar';
import { serverUrl } from 'utils/envUtil';
import { EZTable } from 'components';
import type {
    forwardRefEZTableProps,
    IOnChangeCallbackProps,
    ITableQueryParams,
} from 'components/table/EZProTable';
import { DataTableQueryType } from 'types/base';
import qs from 'qs';

const { Text } = Typography;
const { Option } = Select;

interface Calendar extends APICalendar {
    created_at: string;
    updated_at: string;
}

const CALENDAR_TYPES: Record<CalendarType, string> = {
    [CalendarType.INTERNAL]: 'Internal',
    [CalendarType.EXTERNAL]: 'External',
};

const CALENDAR_PERMISSIONS = [
    {
        value: 1,
        label: 'Busy/Free',
        description: 'Only show busy/free status without details',
    },
    {
        value: 2,
        label: 'Full Details',
        description: 'Show all event details including title, description, and location',
    },
];

const defaultTableParam: ITableQueryParams = {
    searchKeyword: '',
    orderParams: [['created_at', 'desc']],
    whereParams: {},
    whereFilterParams: {},
    silentLoading: false,
};

export function SubTab_CalendarManagement() {
    const [calendars, setCalendars] = useState<Calendar[]>([]);
    const [externalModalVisible, setExternalModalVisible] = useState(false);
    const [internalModalVisible, setInternalModalVisible] = useState(false);
    const [editingCalendar, setEditingCalendar] = useState<Calendar | null>(null);
    const [form] = Form.useForm();
    const EZTableRef = useRef<forwardRefEZTableProps>();
    const [queryParams, setQueryParams] = useState<ITableQueryParams>(defaultTableParam);

    async function _fetchData(params: string): Promise<any> {
        const queryParams = qs.parse(params) as DataTableQueryType;
        return getCalendars(queryParams).then(result => result);
    }

    const _onChangeCallback: IOnChangeCallbackProps = (data: Calendar[]) => {
        if (data?.length) {
            setCalendars(data);
        } else {
            setCalendars([]);
        }
    };

    const handleCreateCalendar = async (values: any) => {
        try {
            // Set calendar_type based on which modal is open
            const calendarData = {
                ...values,
                calendar_type: internalModalVisible ? CalendarType.INTERNAL : CalendarType.EXTERNAL,
            };

            const response = await createCalendar(calendarData);
            if (response.data) {
                message.success('Calendar created successfully');
                setExternalModalVisible(false);
                setInternalModalVisible(false);
                form.resetFields();
                EZTableRef.current?.reload();
            } else {
                message.error('Failed to create calendar');
            }
        } catch (error) {
            message.error('Failed to create calendar');
        }
    };

    const handleUpdateCalendar = async (values: any) => {
        if (!editingCalendar) return;
        try {
            const response = await updateCalendar(editingCalendar.calendar_id, values);
            if (response.data) {
                message.success('Calendar updated successfully');
                setExternalModalVisible(false);
                setInternalModalVisible(false);
                setEditingCalendar(null);
                form.resetFields();
                EZTableRef.current?.reload();
            } else {
                message.error('Failed to update calendar');
            }
        } catch (error) {
            message.error('Failed to update calendar');
        }
    };

    const handleDeleteCalendar = async (calendarId: number) => {
        Modal.confirm({
            title: 'Delete Calendar',
            content: 'Are you sure you want to delete this calendar? This action cannot be undone.',
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    const response = await deleteCalendar(calendarId);
                    if (response.data) {
                        message.success('Calendar deleted successfully');
                        EZTableRef.current?.reload();
                    } else {
                        message.error('Failed to delete calendar');
                    }
                } catch (error) {
                    message.error('Failed to delete calendar');
                }
            },
        });
    };

    const handleSyncCalendar = async (calendar: Calendar) => {
        if (calendar.calendar_type === CalendarType.INTERNAL) {
            message.error('Internal calendars cannot be synced');
            return;
        }

        if (calendar.is_active) {
            Modal.confirm({
                title: 'Sync Calendar',
                content: 'Are you sure you want to sync this calendar manually?',
                okText: 'Sync',
                okType: 'primary',
                cancelText: 'Cancel',
                onOk: async () => {
                    try {
                        const response = await syncCalendar(calendar.calendar_id);
                        if (response.status === 200) {
                            message.success('Calendar sync initiated. Please check back later.');
                            EZTableRef.current?.reload();
                        } else {
                            message.error('Failed to sync calendar');
                        }
                    } catch (error) {
                        message.error('Failed to sync calendar');
                    }
                },
            });
        } else {
            message.error('Calendar is not active');
        }
    };

    const showEditModal = (calendar: Calendar) => {
        setEditingCalendar(calendar);
        form.setFieldsValue(calendar);
        if (calendar.calendar_type === CalendarType.INTERNAL) {
            setInternalModalVisible(true);
        } else {
            setExternalModalVisible(true);
        }
    };

    const handleModalOk = () => {
        form.validateFields()
            .then(values => {
                if (editingCalendar) {
                    handleUpdateCalendar(values);
                } else {
                    handleCreateCalendar(values);
                }
            })
            .catch(error => {
                console.error('Form validation error:', error);
            });
    };

    const copyToClipboard = (text: string) => {
        const fullUrl = `${serverUrl}${text}`;
        navigator.clipboard.writeText(fullUrl);
        message.success('URL copied to clipboard');
    };

    const columns: ProColumns<Calendar>[] = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 200,
            fixed: 'left' as const,
            render: (_, record) => (
                <Text style={{ maxWidth: '180px' }} ellipsis>
                    {record.name}
                </Text>
            ),
        },
        {
            title: 'URL',
            dataIndex: 'url',
            key: 'url',
            width: 250,
            render: (_, record) => (
                <Space>
                    <Tooltip
                        title={`${record.calendar_type === CalendarType.INTERNAL ? serverUrl : ''}${
                            record.url
                        }`}
                    >
                        <Text
                            style={{ maxWidth: '200px' }}
                            ellipsis
                            copyable={{
                                text: `${
                                    record.calendar_type === CalendarType.INTERNAL ? serverUrl : ''
                                }${record.url}`,
                            }}
                        >
                            {record.url}
                        </Text>
                    </Tooltip>
                </Space>
            ),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            width: 300,
            render: (_, record) => (
                <Text style={{ maxWidth: '300px' }} ellipsis>
                    {record.description || '-'}
                </Text>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'is_active',
            key: 'is_active',
            width: 120,
            render: (_, record) => (
                <Tooltip title="Calendar status">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Switch
                            checked={record.is_active}
                            checkedChildren="Active"
                            unCheckedChildren="Inactive"
                            disabled
                        />
                    </div>
                </Tooltip>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'calendar_type',
            key: 'calendar_type',
            width: 120,
            render: (_, record) =>
                CALENDAR_TYPES[record.calendar_type as CalendarType] || 'Unknown',
        },
        {
            title: 'Permission',
            dataIndex: 'calendar_permission',
            key: 'calendar_permission',
            width: 150,
            render: (_, record) => {
                const permissionObj = CALENDAR_PERMISSIONS.find(
                    p => p.value === record.calendar_permission
                );
                return record.calendar_type === CalendarType.INTERNAL && permissionObj ? (
                    <Space>
                        {permissionObj.label}
                        <Tooltip title={permissionObj.description}>
                            <InfoCircleOutlined style={{ color: '#1890ff' }} />
                        </Tooltip>
                    </Space>
                ) : (
                    '-'
                );
            },
        },
        {
            title: 'Created At',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 180,
            render: (_, record) => moment(record.created_at).format('YYYY-MM-DD HH:mm:ss'),
        },
        {
            title: 'Updated At',
            dataIndex: 'updated_at',
            key: 'updated_at',
            width: 180,
            render: (_, record) =>
                record.updated_at ? moment(record.updated_at).format('YYYY-MM-DD HH:mm:ss') : '-',
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right' as const,
            width: 150,
            render: (_, record) => (
                <Space>
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => showEditModal(record)}
                    />
                    <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteCalendar(record.calendar_id)}
                    />
                    <Dropdown
                        trigger={['click']}
                        overlay={
                            <Menu>
                                <Menu.Item
                                    key="2"
                                    disabled={record.calendar_type === CalendarType.INTERNAL}
                                    onClick={() => handleSyncCalendar(record)}
                                >
                                    <ReloadOutlined className="mr-3" />
                                    Sync Calendar
                                </Menu.Item>
                            </Menu>
                        }
                        placement="bottomRight"
                    >
                        <span onClick={e => e.preventDefault()}>
                            <Button size="small" type="link" icon={<EllipsisOutlined />} />
                        </span>
                    </Dropdown>
                </Space>
            ),
        },
    ];

    return (
        <div className="p-4">
            <div className="pr-3 pl-3 text-help mb-3" style={{ textAlign: 'left' }}>
                <InfoCircleOutlined /> Tip: You can integrate external calendars or publish your
                EZCoordinator calendar. Click{' '}
                <Popover
                    content={
                        <Row gutter={[0, 12]} style={{ width: '500px' }}>
                            <Col>
                                <b>Tip #1</b> - Add an external calendar by clicking{' '}
                                <LinkOutlined /> <b>Add External Calendar</b> to view events from
                                Google Calendar, Outlook, or any iCal feed.
                            </Col>
                            <Col>
                                <b>Tip #2</b> - Click <PlusOutlined /> <b>Publish Calendar</b> to
                                share your EZCoordinator calendar with external applications.
                            </Col>
                            <Col>
                                <b>Tip #3</b> - Use the <ReloadOutlined /> <b>Sync Calendar</b>{' '}
                                option to manually refresh external calendar data.
                            </Col>
                            <Col>
                                <b>Tip #4</b> - Control what information is shared by selecting
                                appropriate permissions when publishing your calendar.
                            </Col>
                        </Row>
                    }
                    title="More tips on Calendar Management"
                    trigger="click"
                >
                    <a>here</a>
                </Popover>{' '}
                to see more tips.
            </div>
            <div className="mb-4" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Space>
                    <Button
                        type="primary"
                        icon={<LinkOutlined />}
                        onClick={() => {
                            setEditingCalendar(null);
                            form.resetFields();
                            form.setFieldsValue({
                                calendar_type: CalendarType.EXTERNAL,
                                calendar_permission: 2,
                                is_active: true,
                            });
                            setExternalModalVisible(true);
                        }}
                    >
                        Add External Calendar
                    </Button>
                    <Button
                        type="default"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setEditingCalendar(null);
                            form.resetFields();
                            form.setFieldsValue({
                                calendar_type: CalendarType.INTERNAL,
                                calendar_permission: 2,
                                is_active: true,
                                url: '',
                            });
                            form.setFields([
                                {
                                    name: 'url',
                                    errors: [],
                                },
                            ]);
                            setInternalModalVisible(true);
                        }}
                    >
                        Publish Calendar
                    </Button>
                </Space>
            </div>

            <EZTable
                ref={EZTableRef}
                columns={columns}
                dataSource={calendars}
                rowKey="calendar_id"
                scroll={{ x: 1500 }}
                pagination={{
                    pageSize: 100,
                    pageSizeOptions: [20, 50, 100],
                    position: ['bottomLeft'],
                }}
                sticky
                fetchData={_fetchData}
                onChangeCallback={_onChangeCallback}
                queryParams={queryParams}
            />

            {/* External Calendar Modal */}
            <Modal
                title={editingCalendar ? 'Edit External Calendar' : 'Add External Calendar'}
                visible={externalModalVisible}
                destroyOnClose={true}
                onOk={handleModalOk}
                onCancel={() => {
                    setExternalModalVisible(false);
                    setEditingCalendar(null);
                    form.resetFields();
                }}
            >
                <div className="mb-4">
                    <Typography.Paragraph>
                        Add an external calendar to view its events alongside your EZCoordinator
                        calendar. You can subscribe to calendars from Google Calendar, Outlook, or
                        any other calendar service that provides an iCal feed URL.
                    </Typography.Paragraph>
                </div>

                <Form form={form} layout="vertical">
                    <Form.Item
                        name="name"
                        label="Calendar Name"
                        rules={[{ required: true, message: 'Please input the calendar name!' }]}
                    >
                        <Input placeholder="Enter a name for this calendar" />
                    </Form.Item>

                    {internalModalVisible && !editingCalendar && (
                        <Form.Item name="url" label="Calendar URL" initialValue="">
                            <Input style={{ display: 'none' }} />
                        </Form.Item>
                    )}

                    {(!internalModalVisible ||
                        (editingCalendar &&
                            editingCalendar.calendar_type === CalendarType.EXTERNAL)) && (
                        <Form.Item
                            name="url"
                            label={
                                <span>
                                    Calendar URL{' '}
                                    <Tooltip title="The iCal feed URL of the external calendar you want to subscribe to. This should be a URL that ends with .ics or provides calendar data in iCal format.">
                                        <InfoCircleOutlined style={{ color: '#1890ff' }} />
                                    </Tooltip>
                                </span>
                            }
                            rules={[{ required: true, message: 'Please input the calendar URL!' }]}
                        >
                            <Input placeholder="https://calendar.google.com/..." />
                        </Form.Item>
                    )}

                    <Form.Item
                        name="description"
                        label={
                            <span>
                                Description{' '}
                                <Tooltip title="Add a description to help identify this calendar">
                                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                                </Tooltip>
                            </span>
                        }
                    >
                        <Input.TextArea
                            rows={3}
                            placeholder="Enter a description for this calendar"
                        />
                    </Form.Item>

                    <Form.Item
                        name="is_active"
                        label={
                            <span>
                                Calendar Status{' '}
                                <Tooltip title="Enable or disable the calendar feed. When disabled, the calendar events will not be displayed.">
                                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                                </Tooltip>
                            </span>
                        }
                        valuePropName="checked"
                    >
                        <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Internal Calendar Modal */}
            <Modal
                title={editingCalendar ? 'Edit Published Calendar' : 'Publish Calendar'}
                visible={internalModalVisible}
                onOk={handleModalOk}
                onCancel={() => {
                    setInternalModalVisible(false);
                    setEditingCalendar(null);
                    form.resetFields();
                }}
            >
                <div className="mb-4">
                    <Typography.Paragraph>
                        Share your EZCoordinator calendar with external applications like Google
                        Calendar, Outlook, or Apple Calendar. Your events will automatically sync to
                        the published feed, and you can control what information is shared.
                    </Typography.Paragraph>
                </div>

                <Form form={form} layout="vertical">
                    <Form.Item
                        name="name"
                        label="Calendar Name"
                        rules={[{ required: true, message: 'Please input the calendar name!' }]}
                    >
                        <Input placeholder="Enter a name for this calendar" />
                    </Form.Item>

                    {editingCalendar && editingCalendar.calendar_type === CalendarType.INTERNAL && (
                        <Form.Item label="Calendar URL">
                            <Input
                                value={`${serverUrl}${editingCalendar.url.substring(1)}`}
                                readOnly
                                addonAfter={
                                    <Button
                                        type="text"
                                        icon={<CopyOutlined />}
                                        onClick={() =>
                                            copyToClipboard(
                                                `${serverUrl}${editingCalendar.url.substring(1)}`
                                            )
                                        }
                                    />
                                }
                            />
                        </Form.Item>
                    )}

                    <Form.Item
                        name="calendar_permission"
                        label="Sharing Permission"
                        rules={[
                            { required: true, message: 'Please select the calendar permission!' },
                        ]}
                    >
                        <Select
                            showSearch
                            optionFilterProp="label"
                            placeholder="Select what information to share"
                        >
                            {CALENDAR_PERMISSIONS.map(permission => (
                                <Option
                                    key={permission.value}
                                    value={permission.value}
                                    label={permission.label}
                                >
                                    <div>
                                        <div>{permission.label}</div>
                                        <div style={{ fontSize: '12px', color: '#666' }}>
                                            {permission.description}
                                        </div>
                                    </div>
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label={
                            <span>
                                Description{' '}
                                <Tooltip title="Add a description to help identify this calendar">
                                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                                </Tooltip>
                            </span>
                        }
                    >
                        <Input.TextArea
                            rows={3}
                            placeholder="Enter a description for this calendar"
                        />
                    </Form.Item>

                    <Form.Item
                        name="is_active"
                        label={
                            <span>
                                Calendar Status{' '}
                                <Tooltip title="Enable or disable the calendar feed. When disabled, the calendar URL will not be accessible.">
                                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                                </Tooltip>
                            </span>
                        }
                        valuePropName="checked"
                    >
                        <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
